import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, ChevronLeft, MessageCircle, Ear, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_KEY = { manipulation: 'eiManipulation', indoctrination: 'eiIndoctrination', harassment: 'eiHarassment', lie: 'eiLie', danger: 'eiDanger', related: 'eiRelated' };

/** Déesse history — last 10 conversations (text only). */
export default function DeesseHistory({ T, onBack, onResume }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get('/api/deesse/history');
      setItems(r.data?.conversations || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const view = async (id) => {
    try {
      const r = await axios.get(`/api/deesse/history/${id}`);
      setOpen(r.data);
    } catch { toast.error(T.eiKo); }
  };

  const remove = async (id) => {
    if (!window.confirm(`${T.eiDelete} ?`)) return;
    try {
      await axios.delete(`/api/deesse/history/${id}`);
      setOpen(null);
      load();
    } catch { toast.error(T.eiKo); }
  };

  if (open) {
    return (
      <div data-testid="deesse-history-detail" className="space-y-3">
        <div className="flex items-center justify-between">
          <button onClick={() => setOpen(null)} className="flex items-center gap-1 text-xs text-fuchsia-200" data-testid="deesse-history-detail-back"><ChevronLeft className="w-4 h-4" /> {T.eiBack}</button>
          <div className="flex gap-2">
            {open.mode === 'A' && <button onClick={() => onResume(open.id)} className="px-3 py-1 rounded-full bg-fuchsia-500/20 text-fuchsia-200 text-xs" data-testid="deesse-history-resume">{T.eiModeA}</button>}
            <button onClick={() => remove(open.id)} className="p-1.5 rounded-full bg-red-500/20 text-red-300" data-testid="deesse-history-delete"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
        <h4 className="font-semibold text-sm text-white truncate">{open.title}</h4>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {(open.messages || []).map((m, i) => (
            <div key={i} className={`text-xs p-2.5 rounded-xl ${m.role === 'user' ? 'bg-fuchsia-500/20 ml-8' : m.role === 'heard' ? 'bg-white/5 border border-white/10' : 'bg-black/40 mr-8'}`}>
              {m.role === 'heard' && <span className="text-[10px] uppercase tracking-wide text-gray-400 block mb-0.5">{T.eiHeard}{typeof m.risk === 'number' ? ` · ${T.eiRisk} ${m.risk}%` : ''}</span>}
              {m.content}
            </div>
          ))}
          {(open.alerts || []).length > 0 && (
            <div className="space-y-1.5 pt-2">
              <p className="text-[11px] font-semibold text-red-300 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> {T.eiAlerts}</p>
              {open.alerts.map((a, i) => (
                <div key={i} className="text-xs p-2 rounded-lg bg-red-500/10 border border-red-500/30">
                  <span className="font-semibold text-red-200">{T[ALERT_KEY[a.type]] || a.type}</span> — {a.explanation}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="deesse-history">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-fuchsia-200" data-testid="deesse-history-back"><ChevronLeft className="w-4 h-4" /> {T.eiBack}</button>
        <p className="text-[11px] text-gray-400">{T.eiHistorySub}</p>
      </div>
      {items === null ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-fuchsia-300" /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-xs text-gray-400 py-8" data-testid="deesse-history-empty">{T.eiNoHistory}</p>
      ) : (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {items.map(c => (
            <div key={c.id} className="relative">
            <button onClick={() => view(c.id)} className="w-full text-left p-3 pr-12 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-start gap-3" data-testid={`deesse-history-item-${c.id}`}>
              <div className={`p-2 rounded-lg ${c.mode === 'B' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-fuchsia-500/20 text-fuchsia-200'}`}>
                {c.mode === 'B' ? <Ear className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{c.title}</p>
                <p className="text-[11px] text-gray-400 truncate">{c.preview}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{c.updated_at ? new Date(c.updated_at).toLocaleString() : ''} · {c.messages_count} msg{c.alerts_count ? ` · ${c.alerts_count} ${T.eiAlerts.toLowerCase()}` : ''}</p>
              </div>
            </button>
            <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="absolute right-3 top-3 p-1.5 rounded-full bg-red-500/15 hover:bg-red-500/30 text-red-300" title={T.eiDelete} data-testid={`deesse-history-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
